/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
    Contract,
    GetContractTermsOutput,
    GetContractsByIdOutput,
    ProgramTypeNameEnum,
    PrsDeductionCapTypeEnum,
    Ratchet,
    RatchetModelsEnum,
    RateCard,
    RoyaltyModelsEnum
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { sortBy } from 'lodash';
import { wmgBlendedRateGridColIdSuffix } from 'src/constants/rateCards';
import {
    IAmfContractTerms,
    IPrimeAMFRateCardGridRowData,
    IPrimeAMFRateCardValues,
    IPrimeContractTerms,
    IRateCardGridRowData,
    IRateCardHeaderValues
} from 'src/types/IRateCard';
import { getConversionModel } from 'src/util/RateCards';
import { getThresholdFieldName } from 'src/util/thresholdUtil';

export const mapPrimeAMFContractToRateCard = (
    contractResponse: GetContractsByIdOutput,
    contractTermsResponse: GetContractTermsOutput
): IPrimeAMFRateCardValues => {
    const contract = contractResponse.contracts![0]!;
    const serviceId = contract.serviceId!;

    const rateCardRowData: IPrimeAMFRateCardGridRowData = mapRateCardRowData(contract);

    const householdSharingTerritoryIds = new Set<number>();
    rateCardRowData.cpp.forEach((row) => {
        if (row.programType === ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY) {
            householdSharingTerritoryIds.add(row.territoryId);
        }
    });

    const rateCardHeaderValues: IRateCardHeaderValues = mapRateCardHeaderValues(contract);
    const primeContractTerms = contractTermsResponse.contractTermsUnion?.primeContractTerms;
    const primeContractTermsWithStringValue: IPrimeContractTerms = {
        ...primeContractTerms,
        costPerPlay: {
            ...primeContractTerms?.costPerPlay,
            playTypes: primeContractTerms?.costPerPlay?.playTypes ?? [],
            programTypeList: primeContractTerms?.costPerPlay?.programTypeList ?? [],
            householdSharingTerritoryIds: householdSharingTerritoryIds
        },
        prsDeductionCap: {
            type: primeContractTerms?.prsDeductionCap?.type ?? PrsDeductionCapTypeEnum.ROYALTY,
            value: primeContractTerms?.prsDeductionCap?.value?.toString() ?? '1'
        },
        adRevShare: primeContractTerms?.adRevShare
            ? {
                  ...primeContractTerms.adRevShare,
                  revSharePercent: primeContractTerms.adRevShare.revSharePercent?.toString() ?? '',
                  playTypes: primeContractTerms.adRevShare.playTypes ?? [],
                  territories: primeContractTerms.adRevShare.territories ?? []
              }
            : undefined
    };
    const amfContractTerms = contractTermsResponse.contractTermsUnion?.amfContractTerms;
    const amfContractTermsWithStringValue: IAmfContractTerms = {
        ...amfContractTerms,
        adRevShare: {
            ...amfContractTerms?.adRevShare,
            revSharePercent: amfContractTerms?.adRevShare?.revSharePercent.toString() ?? '',
            playTypes: amfContractTerms?.adRevShare?.playTypes ?? []
        }
    };

    return {
        ...rateCardHeaderValues,
        serviceId,
        primeContractTerms: primeContractTermsWithStringValue,
        amfContractTerms: amfContractTermsWithStringValue,
        rateCardRowData
    };
};

const mapRateCardHeaderValues = (contract: Contract): IRateCardHeaderValues => {
    return {
        contractName: contract.contractName ?? '',
        description: contract.description ?? '',
        isDeleted: contract.isDeleted!,
        isDraft: contract.isDraft!,
        createdBy: contract.createdBy ?? '',
        creationDate: contract.creationDate!,
        changedBy: contract.changedBy,
        changedDate: contract.changedDate,
        entityActiveIntervals: contract.entityActiveIntervals ?? []
    };
};

const mapRateCardRowData = (contract: Contract): IPrimeAMFRateCardGridRowData => {
    let cpp: IRateCardGridRowData[] = [];
    let mau: IRateCardGridRowData[] = [];

    contract.territoryTerms?.forEach((territoryTerm) => {
        territoryTerm.rateCards?.forEach((rateCard: RateCard) => {
            let singleRowData = {
                territoryId: territoryTerm.territoryId,
                settlementCurrency: territoryTerm.settlementCurrency,
                cae: territoryTerm.contentAcquisitionEntity?.contentAcquisitionEntityCode || ''
            } as IRateCardGridRowData;

            singleRowData = {
                ...singleRowData,
                rateCardCurrency: rateCard.currency || '',
                paperPlanName: rateCard.paperPlanName || '',
                playType: rateCard.playType?.playTypeText || '',
                programType: rateCard.programType?.programTypeName || '',
                rate: rateCard.rate?.toString() || ''
            };

            if (rateCard.ratchetModels && rateCard.ratchetModels.length > 0) {
                const conversionModel = getConversionModel(rateCard.ratchetModels);
                const tertiaryConversionModel = getConversionModel(rateCard.ratchetModels, true);

                if (conversionModel) {
                    // handling for UMG Prime relative thresholds
                    if (
                        conversionModel.ratchetModel === RatchetModelsEnum.PRIME_CONVERSION_UMG ||
                        conversionModel.ratchetModel === RatchetModelsEnum.PRIME_CONVERSION_FLOOR_UMG
                    ) {
                        sortBy(
                            [...(conversionModel.ratchets ?? []), { threshold: 1, rate: rateCard.rate }],
                            ['threshold']
                        ).forEach((ratchet: Ratchet, index) => {
                            const thresholdName = getThresholdFieldName(index + 1);
                            singleRowData[thresholdName] = ratchet.rate?.toString() || '';
                        });
                    } else {
                        conversionModel.ratchets?.forEach((ratchet: Ratchet) => {
                            const thresholdName = getThresholdFieldName(ratchet.threshold!);
                            singleRowData[thresholdName] = ratchet.rate?.toString() || '';
                        });
                    }
                }

                if (tertiaryConversionModel) {
                    tertiaryConversionModel.ratchets?.forEach((ratchet: Ratchet) => {
                        const thresholdName = getThresholdFieldName(ratchet.threshold!, wmgBlendedRateGridColIdSuffix);
                        singleRowData[thresholdName] = ratchet.rate?.toString() || '';
                    });
                }
            }

            if (rateCard.royaltyModel?.royaltyModelCode === RoyaltyModelsEnum.CPP) {
                cpp.push(singleRowData);
            } else if (rateCard.royaltyModel?.royaltyModelCode === RoyaltyModelsEnum.MAU) {
                mau.push(singleRowData);
            }
        });
    });

    const uniqueFields = ['territoryId', 'playType', 'programType'];
    // Filter out duplicates of cpp grid row data due to multiple paperPlanName entries
    cpp = cpp.filter(
        (obj1, i, arr) => arr.findIndex((obj2) => uniqueFields.every((key) => obj2[key] === obj1[key])) === i
    );
    // For mau grid, treat paperPlanName/customerType as unique field as it's desired to display rows of different customer types
    mau = mau.filter(
        (obj1, i, arr) =>
            arr.findIndex((obj2) => [...uniqueFields, 'paperPlanName'].every((key) => obj2[key] === obj1[key])) === i
    );

    return {
        cpp,
        mau
    };
};
